import { useMemo, useState } from 'react';
import { GroupBase } from 'react-select';
import ReactSelectCreatable from 'react-select/creatable';
import styled from 'styled-components';

import { useQuery } from '@tanstack/react-query';

import { customTheme, getDefaultStyles } from 'components/shared/form/Select/customStyles';
import { useDebounce } from 'hooks/useDebounce';
import { attrValuesQueryKeys, indexAttrValues } from 'services/api/attrValue';
import { ManualBenefitsPayer, searchExternalPayers } from 'services/api/workflows';
import { colors } from 'styles/theme/colors';
import { BodySmall } from 'styles/typography';

import { PRIVATE_PAY_NAME, VALUE_NAMES } from '../constants';

const MIN_SEARCH_LENGTH = 2;

type PayerOption = ManualBenefitsPayer & { key: string; operatingStates?: string[]; fromDirectory?: boolean };

const PRIVATE_PAY: PayerOption = { key: 'private-pay', type: 'private_pay', name: PRIVATE_PAY_NAME };

// What the API records. A directory pick carries the id the eligibility check runs on and the directory's
// support flag, passed through as it came; Private Pay and a typed name carry neither, so an earlier
// pick's can't ride along. The API refuses a directory pick with no id, so a directory entry that came
// without one is kept by name, as a typed payer would be.
function toPayer({ type, name, stediId, primaryPayerId, eligibilitySupported }: PayerOption): ManualBenefitsPayer {
  if (type !== 'stedi') return { type, name };
  if (!primaryPayerId) return { type: 'other', name };

  return {
    type,
    name,
    stediId: stediId ?? null,
    primaryPayerId,
    eligibilitySupported: eligibilitySupported ?? null,
  };
}

interface Props {
  value: ManualBenefitsPayer | null;
  onChange: (payer: ManualBenefitsPayer | null) => void;
  // The coverage's own payer name. Searched for as soon as the form opens, so its matches are waiting
  // when the menu opens and the directory call is warm before the person starts typing.
  initialQuery?: string;
  disabled?: boolean;
}

export default function PayerTypeahead({ value, onChange, initialQuery, disabled }: Props) {
  const [input, setInput] = useState('');
  const typed = useDebounce(input.trim(), 250);
  // Results for anything but what is in the box now are a previous search's. Offered, they're what a
  // quick Enter picks — a payer the person never typed — so they wait until the search catches up.
  const settled = typed === input.trim();
  const term = typed || (initialQuery ?? '').trim();

  const directory = useQuery({
    queryKey: ['externalPayers', term.toLowerCase()],
    queryFn: ({ signal }) => searchExternalPayers(term, signal),
    enabled: term.length >= MIN_SEARCH_LENGTH,
    retry: false,
    staleTime: 5 * 60 * 1000,
  });

  // Names people have recorded before, typed ones included — the directory doesn't know them. Searched
  // by what the person typed only: the coverage's own name would hide every saved name not containing it.
  const savedParams = {
    workflowValueName: VALUE_NAMES.EXTERNAL_PAYER_NAME,
    distinctDisplayName: true,
    active: true,
    visible: true,
    search: typed || undefined,
    pageSize: 10,
  };
  const saved = useQuery({
    queryKey: [attrValuesQueryKeys.index, 'payerTypeahead', savedParams],
    queryFn: ({ signal }) => indexAttrValues(savedParams, signal),
    staleTime: 60 * 1000,
  });

  const groups = useMemo<GroupBase<PayerOption>[]>(() => {
    const query = input.trim().toLowerCase();
    const directoryOptions: PayerOption[] = (settled ? (directory.data ?? []) : []).map((payer) => ({
      key: `stedi-${payer.stediId ?? payer.name}`,
      type: 'stedi',
      name: payer.name,
      stediId: payer.stediId,
      primaryPayerId: payer.primaryPayerId,
      eligibilitySupported: payer.eligibilitySupported,
      operatingStates: payer.operatingStates,
      fromDirectory: true,
    }));
    const listed = new Set(directoryOptions.map((option) => option.name.toLowerCase()));
    const savedOptions: PayerOption[] = (settled ? (saved.data?.data ?? []) : [])
      .filter(({ name }) => name && name !== PRIVATE_PAY_NAME && !listed.has(name.toLowerCase()))
      // A name once picked from the directory comes back with that pick's payer ID, so it goes back as
      // the pick and network status can match it.
      .map(({ name, data }) =>
        data.externalPayerId
          ? { key: `saved-${name}`, type: 'stedi', name, primaryPayerId: data.externalPayerId }
          : { key: `saved-${name}`, type: 'other', name }
      );

    return [
      // Only while it matches what was typed. Listed first and never filtered, it was the option Enter
      // and Tab picked for any name typed — a person typing a payer got Private Pay.
      { label: '', options: !query || PRIVATE_PAY_NAME.toLowerCase().includes(query) ? [PRIVATE_PAY] : [] },
      { label: 'Previously used', options: savedOptions },
      { label: 'Payer directory', options: directoryOptions },
    ].filter((group) => group.options.length > 0);
  }, [directory.data, saved.data, input, settled]);

  const selected: PayerOption | null = value ? { ...value, key: `value-${value.name}` } : null;
  const listedNames = groups.flatMap((group) => group.options.map((option) => option.name.toLowerCase()));

  // A name typed and left without pressing Enter would otherwise be dropped, leaving Save disabled with
  // nothing to say why. A listed name wins on its stored spelling.
  const commitTyped = () => {
    const typedName = input.trim();
    if (!typedName) return;

    const listed = groups
      .flatMap((group) => group.options)
      .find((option) => option.name.toLowerCase() === typedName.toLowerCase());
    setInput('');
    onChange(listed ? toPayer(listed) : { type: 'other', name: typedName });
  };

  return (
    <div data-testid='manual-benefits-payer'>
      <ReactSelectCreatable<PayerOption, false, GroupBase<PayerOption>>
        inputId='manual-benefits-payer'
        options={groups}
        value={selected}
        inputValue={input}
        onInputChange={(next, meta) => {
          if (meta.action === 'input-change') setInput(next);
        }}
        onBlur={commitTyped}
        onChange={(option) => {
          setInput('');
          onChange(option ? toPayer(option) : null);
        }}
        onCreateOption={(typed) => {
          setInput('');
          onChange({ type: 'other', name: typed.trim() });
        }}
        isValidNewOption={(typed) => typed.trim().length > 0 && !listedNames.includes(typed.trim().toLowerCase())}
        // The default new option is {label, value}, which the name-based label and key below can't read.
        getNewOptionData={(typed) => ({ key: `new-${typed.trim()}`, type: 'other', name: `Add "${typed.trim()}"` })}
        // Filtered on the server: the directory matches aliases the label doesn't show.
        filterOption={() => true}
        getOptionValue={(option) => option.key}
        getOptionLabel={(option) => option.name}
        formatOptionLabel={(option, { context }) =>
          context === 'menu' && option.fromDirectory ? (
            <>
              <div>{option.name}</div>
              <OperatingStates>Operating States: {option.operatingStates?.join(', ') || 'N/A'}</OperatingStates>
            </>
          ) : (
            option.name
          )
        }
        isLoading={directory.isFetching || !settled}
        // Last, so Enter takes the top saved or directory match — and the typed name when nothing
        // matches, including while the directory is still answering.
        allowCreateWhileLoading
        // Tab leaves the field with what was typed (onBlur) rather than picking the focused option.
        tabSelectsValue={false}
        noOptionsMessage={() => 'Type a payer name'}
        placeholder='Search for a payer'
        isDisabled={disabled}
        isClearable
        styles={getDefaultStyles()}
        theme={customTheme}
      />
      {directory.isError && (
        <Unavailable data-testid='payer-search-unavailable'>
          Payer search is unavailable right now — type the payer name instead.
        </Unavailable>
      )}
    </div>
  );
}

const OperatingStates = styled.div`
  font-size: 12px;
  color: ${colors.black50};
`;

const Unavailable = styled(BodySmall)`
  margin-top: 4px;
  color: ${colors.black50};
`;
